import { createContext, useContext, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Route, Routes, useParams } from 'react-router-dom';
import { api } from '../api.js';
import { t, wanneer, geleden, getal } from '../i18n.js';
import { Ernst } from '../components/Status.jsx';
import Vlag from '../components/Vlag.jsx';
import { AlertRijen } from './Alerts.jsx';
import ModuleKaarten from '../components/ModuleKaarten.jsx';
import Uitleg, { Nieuw } from './Uitleg.jsx';
import Versieknop from '../components/Versieknop.jsx';
import Scoreknop from '../components/Scoreknop.jsx';
import Umbrella from './Umbrella.jsx';
import Endpoints from './Endpoints.jsx';
import { StatusVoorKlant } from './Beveiligingsstatus.jsx';
import { StappenVoorKlant } from './VolgendeStappen.jsx';
import logo from '../assets/adts-logo-wit.png';

/**
 * Wat een klant ziet na het aanmelden via het klantportaal (docs/besluiten/klanten-via-het-portaal.md): het overzicht,
 * de alerts en de modules van zijn eigen klant. De server laat weg wat intern is bij ADTS (namen van medewerkers,
 * notities, de reden van afsluiten, meldingen); hier staat dus niets wat die kant niet stuurt. Een onderdeel komt hier
 * pas bij als het voor klanten gebouwd en getest is.
 *
 * **Meekijken** (`meekijken`, docs/besluiten/meekijken-met-een-klant.md): een medewerker ziet hetzelfde scherm voor een
 * klant, via /api/v1/meekijken met dezelfde weglatingen, alleen lezen, onder /meekijken/<klant>.
 */
const Bron = createContext(null);

export default function Klant({ mij, onAfmelden, meekijken = null }) {
  const klant = mij.tenants[0];
  const mag = mij.permissions.includes('alerts.lezen');
  const basis = meekijken ? `/meekijken/${meekijken.code}` : '';
  const bron = meekijken ? {
    basis, meekijken: true, sleutel: ['meekijken', meekijken.code],
    alerts: (status) => api.meekijkAlerts(meekijken.code, status), alert: (n) => api.meekijkAlert(meekijken.code, n),
    modules: async () => ({ data: { modules: meekijken.modules, mayActivate: false } }),
    umbrella: (d) => api.meekijkUmbrella(meekijken.code, d), endpoints: (d) => api.meekijkEndpoints(meekijken.code, d),
    status: () => api.meekijkStatus(meekijken.code),
    score: async () => { const r = await api.meekijkStatus(meekijken.code); return { data: { score: r.data.score, verdict: r.data.verdict, scope: 'klant' } }; },
  } : { basis, meekijken: false, sleutel: ['klant'], alerts: (status) => api.alerts(undefined, status), alert: api.alert, modules: api.mijnModules,
    umbrella: (d) => api.umbrellaGrafieken(undefined, d), endpoints: (d) => api.endpoints(undefined, d), status: api.mijnStatus, score: () => api.mijnScore() };
  /* Umbrella in het menu alleen als die module aan staat. */
  const mods = useQuery({ queryKey: [...bron.sleutel, 'modules'], queryFn: bron.modules, enabled: mag });
  const umbrellaAan = mods.data?.data.modules.some((m) => m.id === 'umbrella' && m.state === 'aan');
  const endpointsAan = mods.data?.data.modules.some((m) => m.id === 'secureendpoint' && m.state === 'aan');
  const item = (naar, label, tekst = t(label)) => <NavLink to={`${basis}${naar}`} end={naar !== '/umbrella'} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>{tekst}</NavLink>;

  return (
    <Bron.Provider value={bron}>
      {meekijken && (
        <div className="meekijkbalk">
          <span>{t('Je kijkt mee als klant met {klant}: zo ziet de klant ADTS ZERO. Alleen lezen.', { klant: klant?.name })}</span>
          <Link to="/beheer">← {t('Terug naar ADTS ZERO')}</Link>
        </div>
      )}
      {/* Dezelfde kop als het klantportaal waar de klant vandaan komt. */}
      <header className="topbar">
        <Link className="brand" to={basis || '/'}>
          <span className="brand-line"><img className="logo" src={logo} alt="ADTS" /><span className="wm-claim">werkt.</span></span>
          <span className="wm-sub">ZERO <span className="licht">Insight</span></span>
        </Link>
        <nav className="nav open" aria-label={t('Hoofdmenu')}>
          <div className="nav-group-items">
            {mag && <>{item('', 'Overzicht')}{item('/status', 'Beveiligingsstatus')}{item('/volgende-stappen', 'Volgende stappen')}{item('/alerts', 'Alerts')}{umbrellaAan && item('/umbrella', null, 'DNS Security')}{endpointsAan && item('/endpoints', 'Secure Endpoint')}{item('/modules', 'Modules')}</>}{item('/uitleg', 'Uitleg')}
          </div>
        </nav>
        <div className="user-area"><div className="user-rij">
          <span className="klantwie"><b>{klant?.name}</b><br />
            {meekijken ? t('meekijken') : mij.email}{mij.roleLabels?.length > 0 && <> · {mij.roleLabels.map((r) => t(r)).join(', ')}</>}</span>
          {mag && <Scoreknop haal={bron.score} sleutel={bron.sleutel} naar={`${basis}/status`} />}
          <Versieknop klant naarAlles={`${basis}/nieuw`} />
          {!meekijken && <a className="nav-item" href={mij.portaal}>{t('Klantportaal')}</a>}
          {!meekijken && <button type="button" className="nav-item" onClick={onAfmelden}>{t('Afmelden')}</button>}
        </div></div>
      </header>
      <main>
        {!mag ? <GeenRechten mij={mij} /> : (
          <Routes>
            <Route index element={<Overzicht klant={klant} />} />
            <Route path="alerts" element={<Lijst />} />
            <Route path="alerts/:nummer" element={<Een />} />
            <Route path="modules" element={<Modules klant={klant} />} />
            <Route path="status" element={<StatusVoorKlant haal={bron.status} sleutel={bron.sleutel} />} />
            <Route path="volgende-stappen" element={<StappenVoorKlant haal={bron.status} sleutel={bron.sleutel} />} />
            <Route path="umbrella" element={<Umbrella haal={bron.umbrella} sleutel={bron.sleutel} sub={klant?.name} />} />
            <Route path="endpoints" element={<Endpoints haal={bron.endpoints} sleutel={bron.sleutel} sub={klant?.name} />} />
            <Route path="uitleg" element={<Uitleg klant versie={mij.version} />} />
            <Route path="nieuw" element={<Nieuw klant versie={mij.version} />} />
            <Route path="*" element={<p className="muted">{t('Niet gevonden')}</p>} />
          </Routes>
        )}
      </main>
    </Bron.Provider>
  );
}

function GeenRechten({ mij }) {
  return (
    <section className="card">
      <header><h2>{t('Aangemeld als {email}, voor {klant}', { email: mij.email ?? '—', klant: mij.tenants[0]?.name ?? '—' })}</h2></header>
      <p className="soft">{t('Je hebt in ADTS ZERO nog geen rol waarmee je iets kunt zien. Vraag ADTS om je een rol te geven.')}</p>
    </section>
  );
}

/** Het overzicht: hoeveel er open staat, wat ADTS nu bekijkt, en wat er laatst is afgehandeld. */
function Overzicht({ klant }) {
  const b = useContext(Bron);
  const open = useQuery({ queryKey: [...b.sleutel, 'alerts', 'open'], queryFn: () => b.alerts(undefined), refetchInterval: 30_000 });
  const alle = useQuery({ queryKey: [...b.sleutel, 'alerts', 'alle'], queryFn: () => b.alerts('alle'), refetchInterval: 60_000 });
  const kop = <div className="page-head"><div><h1>{t('Overzicht')}</h1><p className="sub">{klant?.name}</p></div></div>;
  if (open.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (open.isError) return <>{kop}<div className="melder rood">{t(open.error.message)}</div></>;

  const lijst = open.data.data.alerts;
  const zwaar = lijst.filter((a) => a.severity === 'critical' || a.severity === 'serious').length;
  const bezig = lijst.filter((a) => a.status === 'in behandeling').length;
  const dicht = (alle.data?.data.alerts ?? []).filter((a) => a.status === 'afgesloten').slice(0, 5);
  const tegel = (n, label, extra = '') => <Link className={`tile ${extra}`} to={`${b.basis}/alerts`}><div className="n">{getal(n)}</div><div className="t">{label}</div></Link>;

  return (
    <>
      {kop}
      <div className="tiles">
        {tegel(lijst.length, t('open alerts'), lijst.length ? 'warn' : '')}
        {tegel(zwaar, t('hoog of kritiek'), zwaar ? 'crit' : '')}
        {tegel(bezig, t('ADTS is ermee bezig'))}
        {tegel(open.data.data.counts.nieuw, t('nog niet opgepakt'))}
      </div>
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Open alerts')}</h2><span className="meta"><Link to={`${b.basis}/alerts`}>{t('alle alerts')}</Link></span></header>
        <AlertRijen alerts={lijst} basis={b.basis} />
      </section>
      <section className="card">
        <header><h2>{t('Laatst afgehandeld')}</h2></header>
        {dicht.length ? <AlertRijen alerts={dicht} basis={b.basis} /> : <p className="empty">{t('Nog niets.')}</p>}
      </section>
      <p className="noot" style={{ marginTop: 14 }}>
        {t('ADTS bewaakt de firewalls van {klant} en pakt alerts op. Je ziet hier wat er speelt; oppakken en afsluiten doet ADTS.', { klant: klant?.name })}
      </p>
    </>
  );
}

function Lijst() {
  const b = useContext(Bron);
  const [status, zetStatus] = useState('open');
  const q = useQuery({ queryKey: [...b.sleutel, 'alerts', status], queryFn: () => b.alerts(status === 'alle' ? 'alle' : undefined),
    refetchInterval: 30_000 });
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Alerts')}</h1>
        <p className="sub">{t('Eén alert per voorval. ADTS pakt ze op en sluit ze af.')}</p>
      </div></div>
      <div className="filters">
        <button type="button" className="chip" aria-pressed={status === 'open'} onClick={() => zetStatus('open')}>{t('Open')}</button>
        <button type="button" className="chip" aria-pressed={status === 'alle'} onClick={() => zetStatus('alle')}>{t('Ook afgesloten')}</button>
      </div>
      <section className="card">
        {q.isPending ? <p className="muted">{t('Laden…')}</p>
          : q.isError ? <div className="melder rood">{t(q.error.message)}</div>
          : <AlertRijen alerts={q.data.data.alerts} basis={b.basis} />}
      </section>
    </>
  );
}

/** Eén alert: wat het is, de gegevens uit de eigen syslog, en wat ADTS en ZERO ermee deden (zonder notities). */
function Een() {
  const b = useContext(Bron);
  const { nummer } = useParams();
  const q = useQuery({ queryKey: [...b.sleutel, 'alert', nummer], queryFn: () => b.alert(nummer), refetchInterval: 30_000 });
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) {
    return <><Link className="crumb" to={`${b.basis}/alerts`}>← {t('Alerts')}</Link>
      <p>{q.error.status === 404 ? t('Dit alert bestaat niet.') : t(q.error.message)}</p></>;
  }
  const a = q.data.data;
  const p = a.title.params ?? {};
  const open = a.status !== 'afgesloten';
  const stil = a.rule === 'stilte' || a.rule === 'sensor-stil';
  const waar = [p.land, p.stad, p.asn ? `AS${p.asn}` : null, p.org].filter(Boolean).join(', ');
  const waarden = (w) => (w?.tijd ? { ...w, tijd: wanneer(w.tijd) } : w);
  const stand = !open ? t('Afgesloten door {wie} op {wanneer}.', { wie: a.closed?.by.name ?? 'ADTS', wanneer: wanneer(a.closed?.at) })
    : a.status === 'in behandeling' ? t('ADTS is ermee bezig.')
    : t('ADTS heeft dit alert nog niet opgepakt.');

  return (
    <>
      <Link className="crumb" to={`${b.basis}/alerts`}>← {t('Alerts')}</Link>
      <div className="page-head"><div>
        <h1>{t(a.title.key, a.title.params)}</h1>
        <p className="sub"><Ernst ernst={a.severity} /> · <span className="mono">{a.number}</span>{' '}
          <span className={`pill ${a.status === 'in behandeling' ? 'behandeling' : a.status}`}>{a.resolvedAt && open ? t('hersteld') : t(a.status)}</span></p>
      </div></div>
      <div className={`callout ${open ? 'warn' : 'ok'}`} style={{ marginBottom: 18 }}>{stand}</div>

      <div className="grid-2">
        <section className="card">
          <header><h2>{t('Tijdlijn')}</h2></header>
          <div className="rows">
            {a.timeline.map((e, i) => (
              <div className="row" key={i} style={{ gridTemplateColumns: '130px minmax(0,1fr)' }}>
                <span className="t">{wanneer(e.at)}</span>
                <span>{e.key && <b>{t(e.key, waarden(e.params))}</b>}{e.by && <span className="muted"> · {e.by.name}</span>}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="card">
          <header><h2>{t('Gegevens')}</h2></header>
          <dl className="facts">
            {a.module && <><dt>{t('Module')}</dt><dd>{t(a.module.label)}</dd></>}
            {a.source && <><dt>{t('Bron')}</dt><dd>{a.source.name}</dd></>}
            <dt>{t('Eerst')}</dt><dd>{wanneer(a.firstAt)}</dd>
            <dt>{stil ? t('Laatst stil') : t('Laatst')}</dt><dd>{wanneer(a.lastAt)} · {geleden(a.lastAt)}</dd>
            {p.ip && <><dt>{t('Adres')}</dt><dd><span className="mono">{p.ip}</span>{waar && <span className="muted"> · <Vlag cc={p.land} />{waar}</span>}</dd></>}
            {p.gebruiker && <><dt>{t('Gebruiker')}</dt><dd className="mono">{p.gebruiker}</dd></>}
            {a.rule === 'vpn-aanval' && <><dt>{t('Mislukte logins')}</dt><dd className="num">{p.n}</dd></>}
            {a.rule === 'kwetsbare-versie' && <><dt>{t('Versie')}</dt><dd className="mono">{p.versie}</dd>
              <dt>CVE</dt><dd className="mono">{(p.cves ?? []).join(', ')}</dd></>}
{['ips', 'ips-malware'].includes(a.rule) && <><dt>{a.rule === 'ips' ? t('Melding') : t('Bestand')}</dt><dd className="mono" style={{ overflowWrap: 'anywhere' }}>{p.melding}</dd>
            {a.rule === 'ips' && <><dt>{t('Pogingen')}</dt><dd className="num">{p.n}{p.doorgelaten > 0 && <span className="muted"> · {t('{n} doorgelaten', { n: p.doorgelaten })}</span>}</dd></>}</>}
            {a.rule === 'aaa-onbereikbaar' && <><dt>{t('Server')}</dt><dd className="mono">{p.server} · {p.groep}</dd></>}
            {a.rule === 'beheer-mislukt' && <><dt>{t('Mislukte logins')}</dt><dd className="num">{p.n}<span className="muted"> · {p.namen}</span></dd></>}
            {a.rule === 'beheer-buiten-kantooruren' && <><dt>{t('Handelingen')}</dt><dd>{p.n}<br /><span className="muted" style={{ overflowWrap: 'anywhere' }}>{p.wat}</span></dd></>}
            {a.rule === 'nessus-kritiek' && <><dt>{t('Scan')}</dt><dd>{p.scan}</dd><dt>{t('Bevindingen')}</dt><dd>{p.n} · <span className="muted">{p.plugins}</span></dd></>}
            {a.rule === 'nessus-nieuwe-poort' && <><dt>{t('Scan')}</dt><dd>{p.scan}</dd><dt>{t('Nieuwe poorten')}</dt><dd className="mono">{p.poorten}</dd></>}
            {['umbrella-doorgelaten', 'umbrella-besmet', 'umbrella-geblokkeerd'].includes(a.rule) && <><dt>{t('Apparaat')}</dt><dd>{p.identiteit}</dd>
              <dt>{t('Categorie')}</dt><dd>{p.categorie}</dd><dt>{t('Domeinen')}</dt><dd className="mono" style={{ overflowWrap: 'anywhere' }}>{p.domeinen}</dd></>}
            {a.rule === 'anyconnect-kwetsbaar' && <><dt>{t('Versie')}</dt><dd className="mono">{p.versie} · {p.platform}</dd>
              <dt>CVE</dt><dd className="mono" style={{ overflowWrap: 'anywhere' }}>{(p.cves ?? []).join(', ')}</dd></>}
            {['se-niet-opgeruimd', 'se-ioc', 'se-opgeruimd'].includes(a.rule) && <><dt>{t('Computer')}</dt><dd className="mono">{p.computer}</dd>
              <dt>{t('Wat')}</dt><dd style={{ overflowWrap: 'anywhere' }}>{p.wat}</dd></>}
            {a.rule === 'se-stil' && <><dt>{t('Computers')}</dt><dd>{p.n} / {p.totaal}<br /><span className="muted mono">{p.computers}</span></dd></>}
            {a.rule === 'umbrella-stil' && <><dt>{p.soort === 'Netwerk' ? t('Netwerk') : t('Virtual appliance')}</dt><dd>{p.naam}</dd></>}
            {a.resolvedAt && <><dt>{t('Hersteld')}</dt><dd>{wanneer(a.resolvedAt)}</dd></>}
          </dl>
        </section>
      </div>
    </>
  );
}

/** De modules van de klant: wat aan staat, wat klaar is om aan te zetten, en wat er nog niet is. */
function Modules({ klant }) {
  const b = useContext(Bron);
  const client = useQueryClient();
  const q = useQuery({ queryKey: [...b.sleutel, 'modules'], queryFn: b.modules });
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const doe = (fn, goed) => async (...args) => {
    zetMelding(null); zetBezig(true);
    try { await fn(...args); zetMelding({ goed: true, tekst: goed }); } catch (e) { zetMelding({ goed: false, tekst: t(e.message) }); }
    finally { zetBezig(false); client.invalidateQueries({ queryKey: [...b.sleutel, 'modules'] }); }
  };
  const kop = <div className="page-head"><div><h1>{t('Modules')}</h1><p className="sub">{klant?.name}</p></div></div>;
  if (q.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}<div className="melder rood">{t(q.error.message)}</div></>;
  const { modules, mayActivate } = q.data.data;
  const acties = b.meekijken ? {} : {
    ...(mayActivate ? { schakel: doe((id, aan) => api.zetMijnModule(id, { aan }), t('Opgeslagen.')) } : {}),
    interesse: doe((id) => api.interesseModule(id), t('Doorgegeven aan ADTS. De accountmanager neemt contact op.')),
  };
  return (
    <>
      {kop}
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 14 }}>{melding.tekst}</div>}
      <ModuleKaarten modules={modules} doe={acties} bezig={bezig} />
      <p className="noot" style={{ marginTop: 14 }}>
        {mayActivate || b.meekijken ? t('Een afgenomen module die klaar is, zet je hier zelf aan of uit. Uit betekent: geen nieuwe alerts uit die module; wat er al is, blijft te zien.')
          : t('Aan- of uitzetten doet een beheerder bij jullie (rol Klant: beheerder) of ADTS.')}
      </p>
    </>
  );
}
